import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Badge, Table } from "react-bootstrap";
import { Skeleton, Pagination } from "antd";
import { useIsMutating } from "@tanstack/react-query";
import * as _ from "lodash";
import { useGetOrdersByUser } from "@/hooks/useOrders";
import { useAuth } from "@/_zustand/auth";
import { formatMoney } from "@/lib";
import { sortOrdersByRecency } from "@/lib/ordersSort";
import { estaVencido, textoVigenciaQr } from "@/lib/orderExpiry";
import WhatsappGreen from "@/assets/imgs/whatsappGreen.png";
import SaveIcon from "@/assets/imgs/saveIcon.png";
import "./myorders.css";
import { ShareOrderModal } from "./ShareOrderModal";
import { OrderDetailModal } from "./OrderDetailModal";
import Empty from "@/assets/imgs/empty_orders.png";

export const MyOrdersPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isMutating = useIsMutating({ mutationKey: ["useGetOrdersByUser"] });
  const { mutateAsync: getOrdersMutateAsync } = useGetOrdersByUser();
  const [orders, setOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showModalQr, setShowModalQr] = useState(false);
  const [showModalOrder, setShowModalOrder] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);

  useEffect(() => {
    getOrdersMutateAsync(user.usuario_id)
      .then((resp) => {
        setOrders(Array.isArray(resp) ? resp : []);
      })
      .catch((error) => {
        console.log("error", error);
      });
  }, [getOrdersMutateAsync, user.usuario_id]);

  const getStatusBadge = (status) => {
    switch (status) {
      case "Activo":
        return <Badge bg="primary">{status}</Badge>;
      case "Inactivo":
        return <Badge bg="secondary">{status}</Badge>;
      case "Por retirar":
        return <Badge bg="secondary">{status}</Badge>;
      case "Retirado":
        return <Badge bg="success">{status}</Badge>;
      case "Vencido":
        return <Badge bg="danger">{status}</Badge>;
      case "Pendiente":
        return <Badge bg="warning">{status}</Badge>;
      case "Anulado":
        return <Badge bg="danger">{status}</Badge>;
      case "Despachado":
        return <Badge bg="success">{status}</Badge>;
      case "Retiro parcial":
        return <Badge bg="warning">{status}</Badge>;
      // "Despacho incompleto" es un estado real del backend (OrderDetailModal
      // lo detecta explícitamente para mostrar el aviso de "Retiros
      // incompletos"), pero no estaba en este switch: caía al `default` y,
      // por el typo de abajo, se renderizaba sin ningún fondo — la etiqueta
      // se veía como texto pelado en la tabla. Se trata como los otros
      // estados parciales/de problema.
      case "Despacho incompleto":
        return <Badge bg="warning">{status}</Badge>;
      default:
        // Ojo: decía bg="seconday" (sin la "r"). React-Bootstrap emite la
        // clase tal cual (`badge bg-seconday`), Bootstrap no tiene esa
        // utilidad, y el badge quedaba sin fondo. Afectaba a CUALQUIER estado
        // no listado arriba, no solo a "Despacho incompleto".
        return <Badge bg="secondary">{status}</Badge>;
    }
  };

  // ⭐️ Nueva función auxiliar para generar el enlace
  const generateWhatsappLink = (orderData) => {
    // 1. Construir el mensaje con los datos que tenemos *ahora* (orderData)
    const specificMessage = `Hola, Soy ${user.usuario_nombre} ${user.usuario_apellido}. Necesito ayuda con mi pedido ${orderData.pedido_id}`;

    const genericMessage = `Hola, ¡Espero que estés bien! Soy ${user.usuario_nombre} ${user.usuario_apellido}. Necesito ayuda con mi pedido`;

    // Usamos el mensaje específico si orderData existe, si no, usamos el genérico
    const message = orderData ? specificMessage : genericMessage;

    const encodedMessage = encodeURIComponent(message);

    // Determinar el número de teléfono
    const phoneNumber =
      orderData && orderData.maquina_telefono
        ? orderData.maquina_telefono
        : "56912345678";

    return `https://wa.me/${phoneNumber}?text=${encodedMessage}`;
  };

  const handleShowQr = (order, event) => {
    event.stopPropagation();
    if (estaVencido(order.pedido_vigente)) return;
    setSelectedOrder(order);
    setShowModalQr(true);
  };

  // Más reciente primero (defensa en profundidad, ver src/lib/ordersSort.js)
  const sortedOrders = useMemo(() => sortOrdersByRecency(orders), [orders]);

  // Lógica para calcular los elementos a mostrar en la página actual
  const currentOrders = useMemo(() => {
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    return sortedOrders.slice(indexOfFirstItem, indexOfLastItem);
  }, [sortedOrders, currentPage, itemsPerPage]);

  const handlePageChange = (page, pageSize) => {
    setCurrentPage(page);
    setItemsPerPage(pageSize); // Esto se activa si el Pagination permite cambiar el tamaño de página
  };

  const handleShowDetails = (order) => {
    setSelectedOrder(order);
    setShowModalOrder(true);
  };

  const goToHome = () => {
    const homePath = localStorage.getItem("companyHome") || "/";
    navigate(homePath);
  };

  return (
    <section id="pago" className="container my-5" style={{ flexGrow: 1 }}>
      {/* <QrModal show={showModalQr} order={selectedOrder} onHide={() => setShowModalQr(false)} /> */}
      <ShareOrderModal
        show={showModalQr}
        order={selectedOrder}
        onHide={() => setShowModalQr(false)}
      />
      <OrderDetailModal
        show={showModalOrder}
        order={selectedOrder}
        onHide={() => setShowModalOrder(false)}
      />
      {/* <OrderModal show={showModalOrder} order={selectedOrder} onHide={() => setShowModalOrder(false)} /> */}
      <div className="row">
        <div className="col-12 col-sm-6 col-md-6 col-lg-6 mb-3 inter-regular">
          <h2 className="inter-bold">Mis Pedidos</h2>
        </div>
      </div>
      <div className="row">
        {_.size(orders) > 0 ? (
          <div className="col-12 col-sm-6 col-md-6 col-lg-12 mb-3 inter-regular">
            <Skeleton loading={isMutating} active>
              <div className="tabla-container">
                <div className="tabla-compras">
                  {/* Ant Design Table es muy bueno para esto, pero si usas React-Bootstrap, manténlo */}
                  {/* Si usas Antd Table, la estructura cambia y el componente Pagination se integra mejor */}
                  <Table hover responsive>
                    <thead>
                      <tr>
                        <th>Tienda</th>
                        <th>Estado</th>
                        <th>Artículos</th>
                        <th>Monto</th>
                        <th>Fecha</th>
                        <th>Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentOrders.map((order) => (
                        <tr
                          key={order.pedido_id}
                          onClick={() => handleShowDetails(order)}
                        >
                          {/* Columna "Tienda": el nombre de la tienda, no su
                              direccion. `maquina_nombre` viene del backend
                              (pedidosByEmpresa/usuario/[idUsuario].get.ts:24,
                              que ya resuelve el nombre del grupo cuando el
                              pedido es de un grupo de maquinas); la direccion
                              queda solo como respaldo para pedidos antiguos
                              donde el nombre pueda venir vacio. */}
                          <td>
                            {order.maquina_nombre || order.maquina_direccion}
                          </td>
                          <td>{getStatusBadge(order.pedido_estado)}</td>
                          <td>{order.pedido_nro_articulos}</td>
                          <td>{formatMoney(order.pedido_precio_total)}</td>
                          <td>
                            <div className="fecha">
                              <span>{order.pedido_fecha}</span>
                              <small>
                                {textoVigenciaQr(order.pedido_vigente)}
                              </small>
                            </div>
                          </td>
                          <td className="acciones">
                            <button
                              className="accion-descargar"
                              onClick={(e) => handleShowQr(order, e)}
                              disabled={estaVencido(order.pedido_vigente)}
                              title={
                                estaVencido(order.pedido_vigente)
                                  ? "El QR de este pedido ya venció"
                                  : undefined
                              }
                            >
                              <img src={SaveIcon} alt="carrito" />
                            </button>
                            <button
                              className="accion-whatsapp"
                              onClick={() => {
                                // 1. Opcional: Establece el estado si es necesario para el resto del componente.
                                setSelectedOrder(order);

                                // 2. 🔑 CLAVE: Genera el enlace usando la variable 'order' actual.
                                const dynamicWhatsappLink =
                                  generateWhatsappLink(order);

                                // 3. Abre la ventana con el enlace recién creado.
                                window.open(dynamicWhatsappLink, "_blank");
                              }}
                            >
                              <img src={WhatsappGreen} alt="carrito" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              </div>
            </Skeleton>

            {/* --- Componente de Paginación de Ant Design --- */}
            <div
              style={{
                display: "block",
                margin: "0 auto",
                marginTop: "20px",
                width: "fit-content",
              }}
            >
              <Pagination
                current={currentPage}
                pageSize={itemsPerPage}
                total={sortedOrders.length}
                onChange={handlePageChange}
                showSizeChanger
                pageSizeOptions={["5", "10", "20", "50"]}
              />
            </div>
            {/* --- Fin Componente de Paginación --- */}
          </div>
        ) : (
          <div
            className="col-12 col-sm-12 col-md-12 col-lg-12 mb-3 text-center"
            style={{ padding: "12rem" }}
          >
            <img
              src={Empty}
              alt={"no hay ordenes"}
              className="order-detail-item-img"
            />
            <p>No tienes pedidos aun</p>
            <button className="orders-empty-button" onClick={goToHome}>
              Ir a tiendas
            </button>
          </div>
        )}
      </div>
    </section>
  );
};
